"use client";

import { useState } from "react";
import { Loader, Mail, Phone, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-border bg-muted/50 rounded-md">
        <p className="text-xs uppercase tracking-[0.2em] text-primary mb-4 font-default">Wiadomość wysłana</p>
        <p className="font-default text-xl text-foreground mb-6 leading-relaxed">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-border text-foreground hover:bg-muted hover:text-foreground rounded-md">
          Wyślij kolejne zapytanie
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Imię jest wymagane",
        minLength: {
          value: 2,
          message: "Podaj swoje imię (minimum 2 znaki)"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="text-[11px] uppercase tracking-[0.2em] text-primary font-default font-semibold">Twoje Imię</FormLabel>
              <FormControl>
                <Input placeholder="Jan Kowalski" className="bg-transparent border-0 border-b-2 border-border px-0 h-12 text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary font-default text-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email jest wymagany",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Wpisz prawidłowy adres email"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-2">
              <FormLabel className="text-[11px] uppercase tracking-[0.2em] text-primary font-default font-semibold">Adres Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jan@przyklad.pl" className="bg-transparent border-0 border-b-2 border-border px-0 h-12 text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary font-default text-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className="text-[11px] uppercase tracking-[0.2em] text-primary font-default font-semibold">Numer Telefonu</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+48 000 000 000" className="bg-transparent border-0 border-b-2 border-border px-0 h-12 text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary font-default text-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" rules={{
      required: "Proszę wybrać typ inwestycji"
    }} render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className="text-[11px] uppercase tracking-[0.2em] text-primary font-default font-semibold">Typ Inwestycji</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b-2 border-border px-0 h-12 text-foreground font-default text-lg w-full focus:ring-0">
                  <SelectValue placeholder="Wybierz typ inwestycji" />
                </SelectTrigger>
                <SelectContent className="bg-card border border-border">
                  <SelectItem value="Budowa domu">Budowa domu</SelectItem>
                  <SelectItem value="Roboty ziemne">Roboty ziemne</SelectItem>
                  <SelectItem value="Kostka brukowa">Kostka brukowa</SelectItem>
                  <SelectItem value="Ogrodzenia">Ogrodzenia</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Opis inwestycji jest wymagany",
      minLength: {
        value: 10,
        message: "Opis musi zawierać co najmniej 10 znaków"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-2">
            <FormLabel className="text-[11px] uppercase tracking-[0.2em] text-primary font-default font-semibold">Szczegóły inwestycji</FormLabel>
            <FormControl>
              <Textarea rows={8} placeholder="Opisz swoją wizję, lokalizację i termin realizacji..." className="bg-transparent border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary font-default text-base resize-none rounded-md" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10 rounded-md">
          <p className="text-destructive font-default text-sm">{submitError}</p>
        </div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4">
        <p className="text-[11px] text-muted-foreground font-default max-w-sm leading-relaxed">
          Twoje dane są bezpieczne i wykorzystywane wyłącznie w celu przygotowania wyceny.
        </p>
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[240px] h-14 bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold text-sm uppercase tracking-[0.15em] rounded-md shadow-lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Wysyłanie...
            </> : <>
              Wyślij zapytanie
              <Send className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3528" id="formularz-bezpłatnej-wyceny" className="bg-background text-foreground py-24 md:py-36 px-6 md:px-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--primary)/5%,_transparent_50%)] pointer-events-none" aria-hidden="true" />

      <div ref={ref} className={`max-w-7xl mx-auto relative transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-primary font-default font-semibold mb-8">
                Bezpłatna wycena
              </p>
              <h2 className="font-default font-extrabold text-5xl md:text-6xl lg:text-[64px] leading-[0.95] text-foreground tracking-tight">
                Zbudujmy coś<br />trwałego razem.
              </h2>
            </div>

            <p className="font-default text-lg text-muted-foreground leading-relaxed max-w-sm">
              Prześlij nam szczegóły swojej inwestycji. Nasz zespół przeanalizuje projekt i przygotuje profesjonalną wycenę w ciągu 48 godzin.
            </p>

            <div className="space-y-4 pt-6 border-t border-border">
              <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-default font-semibold">
                Kontakt bezpośredni
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="size-4 text-primary shrink-0" />
                  <Link to="mailto:biuro@budsys.pl" className="font-default text-[15px] text-foreground hover:text-primary transition-colors">biuro@budsys.pl</Link>
                </div>
                <div className="flex items-center gap-3"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone size-4 text-primary shrink-0" aria-hidden="true" zn_id="164"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg><a data-link-type="route" data-link-href="tel:+48500000000" className="font-default text-[15px] text-foreground hover:text-primary transition-colors" href="tel:+48500000000" zn_id="140" contenteditable="false" style={{}}><Link className="underline cursor-pointer" to="/PricingPage">+48 796 280 315</Link> </a></div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="border border-border bg-card p-8 md:p-14 rounded-md shadow-lg">
              <FormProvider formKey="estimate_form_pricing" formId="estimate_form_pricing" sectionName="formularz-bezpłatnej-wyceny" defaultValues={{
              name: "",
              email: "",
              phone: "",
              project_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false
              },
              project_type: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Wyślij zapytanie" successMessage="Gratulujemy! Twoje zapytanie zostało wysłane. Nasz zespół przeanalizuje projekt i przyśle Ci wycenę w ciągu 48 godzin. Dziękujemy, że wybierasz BudSys!" errorMessage="Wystąpił błąd podczas wysyłania. Prosimy spróbować ponownie lub zadzwonić bezpośrednio.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}